/*
 * Header interactions
 * - Mega menus open on hover (with a short intent delay) on devices with a real pointer,
 *   and still open on click / tap / keyboard through the existing <header-menu> details.
 * - Escape closes an open mega menu and returns focus to its toggle.
 * - On the transparent home header, the header turns solid while it is hovered or any of its
 *   menus, the drawer or search is open (see updateHeader in global.js).
 */
(function () {
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  const OPEN_DELAY = 90;
  const CLOSE_DELAY = 200;

  function setSolid(wrapper, solid) {
    const body = document.body;
    if (!body.classList.contains('transparent-header')) return;
    body.classList.toggle('header-solid', solid);
    if (typeof window.updateHeader === 'function') {
      window.updateHeader(window.scrollY);
    } else if (solid || window.scrollY > 37) {
      body.classList.remove('enable-transparency');
    } else {
      body.classList.add('enable-transparency');
    }
  }

  function initHeader(wrapper) {
    if (!wrapper || wrapper.dataset.tlbHeaderInit) return;
    wrapper.dataset.tlbHeaderInit = 'true';

    const menus = Array.from(wrapper.querySelectorAll('.tlb-header header-menu'));
    let hovered = false;

    const isOpen = (menu) => menu.querySelector('details').hasAttribute('open');

    const closeMenu = (menu) => {
      const details = menu.querySelector('details');
      if (!details.hasAttribute('open')) return;
      details.removeAttribute('open');
      details.querySelector('summary').setAttribute('aria-expanded', 'false');
    };

    const refreshSolid = () => {
      const somethingOpen = wrapper.querySelector('details[open]') !== null;
      setSolid(wrapper, hovered || somethingOpen);
    };

    menus.forEach((menu) => {
      const details = menu.querySelector('details');
      const summary = details.querySelector('summary');
      let openTimer;
      let closeTimer;
      let openedByHoverAt = 0;

      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');

      const open = () => {
        menus.forEach((other) => other !== menu && closeMenu(other));
        if (details.hasAttribute('open')) return;
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
        openedByHoverAt = Date.now();
      };

      menu.addEventListener('mouseenter', () => {
        if (!hoverCapable.matches) return;
        clearTimeout(closeTimer);
        // Moving between top-level items while a panel is open switches instantly.
        const switching = menus.some((other) => other !== menu && isOpen(other));
        openTimer = setTimeout(open, switching ? 0 : OPEN_DELAY);
      });

      menu.addEventListener('mouseleave', () => {
        if (!hoverCapable.matches) return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => closeMenu(menu), CLOSE_DELAY);
      });

      summary.addEventListener('click', (event) => {
        // A click right after hover-open would otherwise immediately close the panel.
        if (hoverCapable.matches && details.hasAttribute('open') && Date.now() - openedByHoverAt < 700) {
          event.preventDefault();
          openedByHoverAt = 0;
          return;
        }
        if (!details.hasAttribute('open')) {
          menus.forEach((other) => other !== menu && closeMenu(other));
        }
        summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'false' : 'true');
      });

      details.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() !== 'ESCAPE') return;
        if (!details.hasAttribute('open')) return;
        closeMenu(menu);
        summary.focus();
      });
    });

    wrapper.addEventListener('mouseenter', () => {
      if (!hoverCapable.matches) return;
      hovered = true;
      refreshSolid();
    });

    wrapper.addEventListener('mouseleave', () => {
      hovered = false;
      // Wait for any hover-close so the header doesn't flash transparent under a closing panel.
      setTimeout(refreshSolid, CLOSE_DELAY + 20);
    });

    // `toggle` doesn't bubble, so listen in the capture phase for every details in the header.
    wrapper.addEventListener('toggle', refreshSolid, true);
  }

  // Menu drawer: top-level sections are tabs, each showing its own panel.
  function initDrawerTabs(wrapper) {
    const tabs = Array.from(wrapper.querySelectorAll('[data-drawer-tab]'));
    if (!tabs.length) return;

    const select = (tab, focus) => {
      tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      tab.addEventListener('click', () => select(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 1 : -1;
        select(tabs[(index + step + tabs.length) % tabs.length], true);
      });
    });
  }

  function init() {
    document.querySelectorAll('.header-wrapper').forEach((wrapper) => {
      initHeader(wrapper);
      initDrawerTabs(wrapper);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const wrapper = event.target.querySelector('.header-wrapper');
    if (wrapper) {
      initHeader(wrapper);
      initDrawerTabs(wrapper);
    }
  });
})();
